import { Match, onMount, Switch } from "solid-js";
import { useModal } from "~/components/modal";
import WelcomeMessage from "~/components/welcome-message";
import { openCoinsCreditedMessageTelescope } from "~/components/webstore_telescope/welcome_message_telescope";
import { RNREvent } from "~/data/events";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { QueryParam } from "~/types";
import {
  clientIdConfig,
  enableSingleBrandHomepage,
} from "~/data/partner_config";
import { useWebStoreContext } from "~/context/webstore_context";
import HomeComponentV1 from "~/views/webstore/home_v1/home_v1";
import TataDigitalHomeComponent from "~/views/webstore/home_tata_tigital/td_home";
import HomeTelescopeRnr from "~/views/rnr_telescope/home_v3_telescope/home_v3_telescope";
import { HomepageSingleBrand } from "~/views/rnr_telescope/homepage_single_brand/homepage_single_brand_view";
import HomeWebstoreTelescope from "~/views/webstore/home_webstore_telescope/home_webstore_telescope";
import AxisHomePage from "~/views/webstore/axis_home_page/axis_home_page";
import HomeIndriyaView from "~/views/rnr_telescope/home_indriya_view";
import {
  IciciIndriyaTncs,
  IndriyaTncs,
} from "~/data/partner_faqs/indriya_tncs";
import HomeOnecardView from "~/views/rnr_telescope/home_onecard/home_onecard";
import { COINS_CREDITED_POPUP_KEY } from "~/data/consts";

function hasWelcomePopupBeenDisplayed(): boolean {
  if (typeof window === "undefined") return false; // SSR safety
  try {
    return sessionStorage.getItem(COINS_CREDITED_POPUP_KEY) === "true";
  } catch (error) {
    console.warn("Failed to read from sessionStorage:", error);
    return false;
  }
}

function markWelcomePopupAsDisplayed(): void {
  if (typeof window === "undefined") return; // SSR safety
  try {
    sessionStorage.setItem(COINS_CREDITED_POPUP_KEY, "true");
  } catch (error) {
    console.warn("Failed to write to sessionStorage:", error);
  }
}

/**
 * Hook to manage the welcome popup that displays when users receive new coins.
 * Handles session persistence to prevent showing the popup multiple times during navigation.
 */
function useWelcomePopup() {
  const { setIsModalOn, updateModalContentGetter, setOuterFrameClass } =
    useModal()!;
  const { getClientId, storeState, isDesignSystemV2Enabled } =
    useWebStoreContext();

  // WelcomeMessage popup logic - skip for Axis and Tata clients
  onMount(() => {
    const clientId = getClientId();

    // Skip welcome popup for Axis and Tata clients
    if (
      clientId === clientIdConfig.axisClientId ||
      clientId === clientIdConfig.tdClientId ||
      clientId === clientIdConfig.rpTechClientId ||
      clientId === clientIdConfig.indriyaClientId ||
      clientId === clientIdConfig.iciciIndriyaClientId ||
      clientId === clientIdConfig.mastercardIndriyaClientId
    ) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const showDummyPopup = searchParams.get(QueryParam.Popup) == "1";
    const newCoinAdditionDetails =
      storeState.v4HomePage?.coins?.newCoinAdditionDetails;

    if (
      (newCoinAdditionDetails &&
        newCoinAdditionDetails?.amount &&
        !newCoinAdditionDetails?.seen &&
        newCoinAdditionDetails!.amount! > 0 &&
        !hasWelcomePopupBeenDisplayed()) ||
      showDummyPopup
    ) {
      // Only mark as displayed if NOT in demo mode
      markWelcomePopupAsDisplayed();

      // Calculate total balance
      const totalBalance = storeState.v4HomePage?.coins?.totalAvailable ?? 0;

      // Use telescope version if design system v2 is enabled
      if (isDesignSystemV2Enabled()) {
        const isSingleBrand =
          storeState.brands?.length === 1 &&
          enableSingleBrandHomepage(getClientId());
        const singleBrandName = isSingleBrand
          ? storeState.brands[0].voucherProduct.title
          : undefined;

        setTimeout(() => {
          openCoinsCreditedMessageTelescope({
            coinsCredited: newCoinAdditionDetails?.amount ?? 0,
            userName: storeState.v4HomePage?.user?.firstName ?? "",
            totalBalance: totalBalance,
            singleBrandName,
          });
        }, 1000);
      } else {
        // Legacy modal system
        setOuterFrameClass("md:w-[380px] md:h-[516px]");
        updateModalContentGetter(() => {
          return (
            <WelcomeMessage
              coinsCredited={newCoinAdditionDetails?.amount ?? 0}
              userName={storeState.v4HomePage?.user?.firstName ?? ""}
              title={
                newCoinAdditionDetails?.eventDetails.title ?? "Congratulations"
              }
              subtitle={newCoinAdditionDetails?.eventDetails.description ?? ""}
              showDummy={showDummyPopup}
            />
          );
        });
        setIsModalOn(true);
      }
    }
  });
}

export default function Home() {
  const { getClientId, storeState, isDesignSystemV2Enabled, getMode } =
    useWebStoreContext();
  const analyticsService = useAnalyticsService();

  // Handle welcome popup logic
  useWelcomePopup();

  onMount(() => {
    analyticsService.logEvent(RNREvent.VISIT_HOME_PAGE);
  });

  // new telescope design system
  // HomepageSingleBrand - Brand page-like homepage when only 1 brand is available and enabled for the client
  // HomeTelescopeRnr - New Telescope design system version for compatible clients

  // AxisHomePage - Axis specific homepage
  // TataDigitalHomeComponent - Similar to webstore but with a click to see more brands features, different UI
  // HomeComponentV1 - show all brands , search and categories, used in webstore, most rnr clients

  if (getClientId() === clientIdConfig.axisClientId) {
    return <AxisHomePage />;
  }

  // this is axisbank X indriya campaign
  if (getClientId() === clientIdConfig.indriyaClientId) {
    return (
      <HomeIndriyaView
        offerText="Swipe your Axis Bank Mastercard at Indriya and enjoy ₹1,000 off on spends of ₹1,00,000 and above"
        tncs={IndriyaTncs}
      />
    );
  }

  // this is mastercard X indriya campaign
  if (getClientId() === clientIdConfig.mastercardIndriyaClientId) {
    return (
      <HomeIndriyaView
        offerText="Swipe your Axis Bank Mastercard at Indriya and enjoy ₹1,000 off on spends of ₹1,00,000 and above"
        tncs={IndriyaTncs}
      />
    );
  }

  // this is icici X indriya campaign
  if (getClientId() === clientIdConfig.iciciIndriyaClientId) {
    return (
      <HomeIndriyaView
        offerText="Use your ICICI Bank Mastercard at Indriya to enjoy an extra 5% off on gold Jewellery making charges, in addition to existing in-store offers"
        tncs={IciciIndriyaTncs}
      />
    );
  }

  if (getClientId() === clientIdConfig.tdClientId) {
    return <TataDigitalHomeComponent />;
  }

  // minimal homepage: brand grid only (no categories / separators)
  if (getClientId() === clientIdConfig.onecardClientId) {
    return <HomeOnecardView />;
  }

  if (getMode() == "hubble") {
    // webstore homepage
    return <HomeWebstoreTelescope />;
  }

  // handle for other rnr clients
  return (
    <Switch>
      <Match
        when={
          storeState.brands?.length === 1 &&
          enableSingleBrandHomepage(getClientId())
        }
      >
        <HomepageSingleBrand brandKey={storeState.brands[0].brandKey} />
      </Match>
      <Match when={true}>
        <HomeTelescopeRnr />
      </Match>
    </Switch>
  );
}
